import { useState } from 'react'
import {
  Stack,
  Text,
  DetailsList,
  IColumn,
  SelectionMode,
  DetailsListLayoutMode,
  Modal,
  IconButton,
  Label,
} from '@fluentui/react'
import { cleanMetricName } from '../../utils/metrics'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { LLMTestResult, LLMMetricScore } from '../../api/models'
import ChartFromJson, { isChartJson } from './ChartFromJson'

type TestCaseTableProps = {
  results: LLMTestResult[]
  date: string
}

const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    flex: 1,
  },
}

const TestCaseTable = ({ results, date }: TestCaseTableProps) => {
  const [selectedCase, setSelectedCase] = useState<LLMTestResult | null>(null)

  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'Test Case',
      fieldName: 'name',
      minWidth: 200,
      maxWidth: 350,
      isMultiline: true,
    },
    {
      key: 'success',
      name: 'Status',
      fieldName: 'success',
      minWidth: 70,
      maxWidth: 80,
      onRender: (item: LLMTestResult) => (
        <Text styles={{ root: { fontWeight: 600, color: item.success ? '#5B8A72' : '#D4775B' } }}>
          {item.success ? 'PASS' : 'FAIL'}
        </Text>
      ),
    },
    ...getMetricColumns(results),
  ]

  return (
    <Stack styles={cardStyle} tokens={{ childrenGap: 12 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
          Test Cases — {date}
        </Text>
        <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>
          {results.length} test{results.length !== 1 ? 's' : ''}
        </Text>
      </Stack>
      {results.length > 0 ? (
        <DetailsList
          items={results}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          isHeaderVisible={true}
          onRenderRow={(props, defaultRender) =>
            props && defaultRender ? (
              <div
                onClick={() => setSelectedCase(props.item as LLMTestResult)}
                style={{ cursor: 'pointer' }}
              >
                {defaultRender(props)}
              </div>
            ) : null
          }
          onShouldVirtualize={() => false}
        />
      ) : (
        <Text styles={{ root: { color: '#a19f9d', textAlign: 'center', padding: 40 } }}>
          No test cases found.
        </Text>
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedCase}
        onDismiss={() => setSelectedCase(null)}
        isBlocking={false}
        styles={{
          main: {
            maxWidth: 800,
            width: '90vw',
            borderRadius: 8,
            overflow: 'hidden',
          },
          scrollableContent: {
            padding: 24,
            overflowY: 'auto',
            maxHeight: '85vh',
          },
        }}
      >
        {selectedCase && (
          <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                {selectedCase.name}
              </Text>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                ariaLabel="Close"
                onClick={() => setSelectedCase(null)}
              />
            </Stack>

            <Text
              styles={{
                root: {
                  fontWeight: 600,
                  color: selectedCase.success ? '#5B8A72' : '#D4775B',
                  fontSize: 14,
                },
              }}
            >
              {selectedCase.success ? 'PASSED' : 'FAILED'}
            </Text>

            <Stack tokens={{ childrenGap: 4 }}>
              <Label>Input</Label>
              <div className="markdown-content" style={textBlockStyle}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {selectedCase.input}
                </ReactMarkdown>
              </div>
            </Stack>

            <Stack tokens={{ childrenGap: 4 }}>
              <Label>Actual Output</Label>
              {isChartJson(selectedCase.actual_output) ? (
                <ChartFromJson chartData={JSON.parse(selectedCase.actual_output.trim())} />
              ) : (
                <div className="markdown-content" style={textBlockStyle}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {selectedCase.actual_output}
                  </ReactMarkdown>
                </div>
              )}
            </Stack>

            <Stack tokens={{ childrenGap: 4 }}>
              <Label>Expected Output</Label>
              {isChartJson(selectedCase.expected_output) ? (
                <ChartFromJson chartData={JSON.parse(selectedCase.expected_output.trim())} />
              ) : (
                <div className="markdown-content" style={textBlockStyle}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {selectedCase.expected_output}
                  </ReactMarkdown>
                </div>
              )}
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
              <Label>Metrics</Label>
              {selectedCase.metrics.map((m: LLMMetricScore, i: number) => (
                <Stack
                  key={i}
                  styles={{
                    root: {
                      padding: '12px 16px',
                      backgroundColor: m.success ? '#f0f7f2' : '#fdf2ef',
                      borderRadius: 6,
                      borderLeft: `3px solid ${m.success ? '#5B8A72' : '#D4775B'}`,
                    },
                  }}
                  tokens={{ childrenGap: 4 }}
                >
                  <Stack horizontal horizontalAlign="space-between">
                    <Text styles={{ root: { fontWeight: 600 } }}>{cleanMetricName(m.name)}</Text>
                    <Text styles={{ root: { fontWeight: 600, color: m.success ? '#5B8A72' : '#D4775B' } }}>
                      {(m.score * 100).toFixed(1)}% (threshold: {(m.threshold * 100).toFixed(0)}%)
                    </Text>
                  </Stack>
                  {m.reason && (
                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                      {m.reason}
                    </Text>
                  )}
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}

const textBlockStyle: React.CSSProperties = {
  padding: '10px 12px',
  backgroundColor: '#f3f2f1',
  borderRadius: 4,
  border: '1px solid #edebe9',
  fontSize: 13,
  lineHeight: '1.6',
  color: '#323130',
  userSelect: 'text',
}

function getMetricColumns(results: LLMTestResult[]): IColumn[] {
  // Collect all unique metric names across all test cases
  const metricNames = new Set<string>()
  for (const r of results) {
    for (const m of r.metrics) {
      metricNames.add(m.name)
    }
  }

  return Array.from(metricNames).map((name) => ({
    key: name,
    name: cleanMetricName(name),
    fieldName: name,
    minWidth: 120,
    maxWidth: 160,
    onRender: (item: LLMTestResult) => {
      const metric = item.metrics.find((m) => m.name === name)
      if (!metric) return <Text styles={{ root: { color: '#a19f9d' } }}>—</Text>
      return (
        <Text styles={{ root: { fontWeight: 600, color: metric.success ? '#5B8A72' : '#D4775B' } }}>
          {(metric.score * 100).toFixed(1)}%
        </Text>
      )
    },
  }))
}

export default TestCaseTable
